import { ErrorBoundary } from "@sentry/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { setSpecificParameter } from "../../../../actions";
import { fetchVocAfterSalesFeedbackComments, fetchVocAfterSalesFeedbackCommentsFilters, fetchVocQuickSalesFeedbackComments, fetchVocQuickSalesFeedbackCommentsFilters } from "../../../../actions/vocActions";
import { ErrorMsg, NoDataMsg } from "../../../../components/AppMessages";
import { InAppMultiSelectFilter } from "../../../../components/Filters/common/InAppMultiselectFilter";
import { closeFilterOptions } from "../../../../components/Filters/subs/helpers";
import LoadingEllipsis from "../../../../components/Loading/LoadingEllipsis";
import { DefaultTable } from "../../../../components/Tables/subs";
import { ToggleSwitch } from "../../../../components/Toogle";


interface Props {
  quick_sales?: boolean;
}

const VerbatimTable = withRouter((props: RouteComponentProps & Props) => {
  const { history, quick_sales } = props;

  const dispatch = useDispatch();

  const data = useSelector((state: RootStateOrAny) => quick_sales ? state.voc.voc_quick_sales_feedback_comments : state.voc.voc_after_sales_feedback_comments);
  const {  google_ratings, topics, touchpoints } = useSelector((state: RootStateOrAny) => quick_sales ? state.voc.voc_quick_sales_feedback_comments_filters ?? {} : state.voc.voc_after_sales_feedback_comments_filters ?? {});
  
  const loading = useSelector((state: RootStateOrAny) => quick_sales ? state.loading.voc_quick_sales_feedback_comments : state.loading.voc_after_sales_feedback_comments);

  const [touchpointFilterValue, setTouchpointFilterValue] = useState("All");
  const [topicFilterValue, setTopicFilterValue] = useState("All");
  const [googleRatingFilterValue, setGoogleRatingFilterValue] = useState("All");
  const [toggleValue, setToggleValue] = useState(true);
  const touchpointParam = "touchpoint";
  const topicParam = "topic";
  const googleRatingParam = "google_rating";

  useEffect(() => {
    const googleRating = googleRatingFilterValue == "All" ? [] : googleRatingFilterValue.split(",");
    const topics = topicFilterValue == "All" ? [] : topicFilterValue.split(",");
    const touchpoints = touchpointFilterValue == "All" ? [] : touchpointFilterValue.split(",");
    dispatch(quick_sales ? fetchVocQuickSalesFeedbackComments(googleRating, topics, touchpoints) : fetchVocAfterSalesFeedbackComments(googleRating, topics, touchpoints));
  }, [history.location.search, touchpointFilterValue, topicFilterValue, googleRatingFilterValue, quick_sales]);

  useEffect(() => {
    dispatch( quick_sales ? fetchVocQuickSalesFeedbackCommentsFilters() : fetchVocAfterSalesFeedbackCommentsFilters() );
  }, [history.location.search, quick_sales]);

  console.log(data)

  const columns = useMemo(
    () => [
      {
        id: "market",
        Header: "Market",
        accessor: "market",
      },
      {
        id: "date",
        Header: "Date",
        accessor: "date",
      },
      {
        id: "experienceRating",
        Header: "Satisfaction Score",
        accessor: "satisfaction_score",
      },
      {
        id: "topic",
        Header: "Topic",
        accessor: "topic",
      },
      
      {
        id: "sentiment",
        Header: "Sentiment",
        accessor: "sentiment",
        Cell: (cell: { value: string }) => {
          const { value } = cell;
          return value ? value : "n/a";
        },
      },
      {
        id: "comment",
        Header: "Comment",
        accessor: `${toggleValue ? "verbatim_concat_en" : "verbatim_concat"}`,
      },
    ],
    [data, toggleValue]
  );

  const handleTouchpointMultiselectSubmit = useCallback(() => {
    const parameterName = touchpointParam;
    const selectedCheckboxes: NodeListOf<HTMLInputElement> = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-options-type="single"]:checked`
    );
    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => selectedValues.push(checkbox.value));

    const filterValue = touchpoints?.length === selectedValues?.length ? "All" : selectedValues?.join(",");
    closeFilterOptions();
    setTouchpointFilterValue(filterValue);
    dispatch(setSpecificParameter(parameterName, filterValue));
  }, [touchpoints]);

  const handleTopicMultiselectSubmit = useCallback(() => {
    const parameterName = topicParam;
    const selectedCheckboxes: NodeListOf<HTMLInputElement> = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-options-type="single"]:checked`
    );
    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => selectedValues.push(checkbox.value));

    const filterValue = topics?.length === selectedValues?.length ? "All" : selectedValues?.join(",");
    closeFilterOptions();
    setTopicFilterValue(filterValue);
    dispatch(setSpecificParameter(parameterName, filterValue));
  }, [topics]);

  const handleGoogleRatingMultiselectSubmit = useCallback(() => {
    const parameterName = googleRatingParam;
    const selectedCheckboxes: NodeListOf<HTMLInputElement> = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-options-type="single"]:checked`
    );
    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => selectedValues.push(checkbox.value));

    const filterValue = google_ratings?.length === selectedValues?.length ? "All" : selectedValues?.join(",");
    closeFilterOptions();
    setGoogleRatingFilterValue(filterValue);
    dispatch(setSpecificParameter(parameterName, filterValue));
  }, [google_ratings]);

  const filteredTopicVerbatimData = useMemo(() => {
    if (data) {
      let dataCopy = [...data];
      dataCopy = data.filter(
        (item: any) =>
          (topicFilterValue == "All" ? true : topicFilterValue.includes(item.topic)) &&
          (googleRatingFilterValue == "All" ? true : googleRatingFilterValue.includes(String(item.satisfaction_score)))
      );
      return dataCopy;
    }
    return [];
  }, [topicFilterValue, googleRatingFilterValue, data]);

  

  const totalVerbatimCount = useMemo(() => {
    if (data && data?.length > 0) {
      return data[0]?.verbatim_concat_count;
    }
    return null;
  }, [data]);

  return (
    <div>
      <div className="row grid">
        <div className="tile h-30 grid-col-span-6 verbatimTile">
          <div className="space-between verbatim-tile-header">
            <div className="tileHeader">
              <p>Verbatim</p>
            </div>
            <div className="flex-row">
              <div className="verbatim-count">Total number of verbatims: {totalVerbatimCount ? totalVerbatimCount : "n/a"}</div>
              <ErrorBoundary fallback={<ErrorMsg />}>
                <InAppMultiSelectFilter
                  filterLabel="Select Satisfaction Score"
                  optionsList={google_ratings?.map((rating: number) => String(rating)) ?? []}
                  filterValue={googleRatingFilterValue}
                  parameterName={googleRatingParam}
                  parameterValue={googleRatingFilterValue}
                  handleFilterSubmission={handleGoogleRatingMultiselectSubmit}
                />
              </ErrorBoundary>
              <ErrorBoundary fallback={<ErrorMsg />}>
                <InAppMultiSelectFilter
                  filterLabel="Select Topics"
                  optionsList={topics ?? []}
                  filterValue={topicFilterValue}
                  parameterName={topicParam}
                  parameterValue={topicFilterValue}
                  handleFilterSubmission={handleTopicMultiselectSubmit}
                />
              </ErrorBoundary>              
              <ToggleSwitch
                activeToggleLabel={"English"}
                inactiveToggleLabel={"Native"}
                active={toggleValue}
                handleToggleClick={() => setToggleValue((prevState) => !prevState)}
              />
            </div>
          </div>
          {loading ? (
            <LoadingEllipsis isLoading={loading} />
          ) : filteredTopicVerbatimData ? (
            <DefaultTable
              columns={columns}
              data={filteredTopicVerbatimData}
              id={"topicVerbatimTable"}
              isChildComponent={false}
              initialSortColId={{ id: "market", desc: false }}
              hiddenCols={[]}
              extraClass="verbatimTable"
              showPagination={true}
              pageSize={50}
            />
          ) : (
            <NoDataMsg />
          )}
        </div>
      </div>
    </div>
  );
});

export default VerbatimTable;
